import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Storage as StorageIcon,
  InsertDriveFile as FileIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Mock data for dashboard
const mockData = {
  storageUsage: {
    total: 100 * 1024 * 1024 * 1024, // 100GB
    used: 67.5 * 1024 * 1024 * 1024, // 67.5GB
    percentage: 67.5
  },
  topUsers: [
    { name: '김민수', department: '개발팀', usage: 15.2 * 1024 * 1024 * 1024, avatar: 'KM' },
    { name: '이영희', department: '디자인팀', usage: 12.8 * 1024 * 1024 * 1024, avatar: 'LY' },
    { name: '박준호', department: '마케팅팀', usage: 11.3 * 1024 * 1024 * 1024, avatar: 'PJ' },
    { name: '최지혜', department: '기획팀', usage: 9.7 * 1024 * 1024 * 1024, avatar: 'CJ' },
    { name: '정수현', department: '영업팀', usage: 8.9 * 1024 * 1024 * 1024, avatar: 'JS' },
    { name: '한성민', department: 'HR팀', usage: 7.2 * 1024 * 1024 * 1024, avatar: 'HS' },
    { name: '오경아', department: '재무팀', usage: 6.8 * 1024 * 1024 * 1024, avatar: 'OK' }
  ],
  storageHistory: [
    { month: '01월', usage: 45 },
    { month: '02월', usage: 52 },
    { month: '03월', usage: 48 },
    { month: '04월', usage: 58 },
    { month: '05월', usage: 63 },
    { month: '06월', usage: 67.5 }
  ],
  fileTypes: [
    { name: 'PDF', value: 35, color: '#ff6b6b' },
    { name: 'Excel', value: 25, color: '#4ecdc4' },
    { name: 'Word', value: 20, color: '#45b7d1' },
    { name: 'PowerPoint', value: 12, color: '#96ceb4' },
    { name: '기타', value: 8, color: '#feca57' }
  ],
  largeFiles: [
    { name: '2024년도 사업계획서.pptx', size: '125.3 MB', lastModified: '2024-01-15', owner: '김민수' },
    { name: '제품카탈로그_최종.pdf', size: '98.7 MB', lastModified: '2024-01-10', owner: '이영희' },
    { name: '고객데이터베이스.xlsx', size: '87.2 MB', lastModified: '2024-01-08', owner: '박준호' },
    { name: '마케팅영상_HD.mp4', size: '156.8 MB', lastModified: '2024-01-05', owner: '최지혜' },
    { name: '교육자료_전체.zip', size: '203.5 MB', lastModified: '2024-01-03', owner: '정수현' },
    { name: '회계시스템_백업.sql', size: '345.7 MB', lastModified: '2024-01-02', owner: '한성민' },
    { name: '프로젝트_아카이브.tar', size: '412.8 MB', lastModified: '2024-01-01', owner: '오경아' }
  ],
  recentFiles: [
    { name: '회의록_0121.docx', lastModified: '1시간 전', owner: '김민수', type: 'word' },
    { name: '월간보고서.xlsx', lastModified: '3시간 전', owner: '이영희', type: 'excel' },
    { name: '신제품기획서.pdf', lastModified: '5시간 전', owner: '박준호', type: 'pdf' },
    { name: 'UI목업파일.psd', lastModified: '1일 전', owner: '최지혜', type: 'image' }
  ],
  topDownloads: [
    { name: '신입사원교육자료.pdf', downloads: 847, owner: '김인사' },
    { name: '회사소개서.pptx', downloads: 623, owner: '이마케팅' },
    { name: '업무매뉴얼.docx', downloads: 456, owner: '박기획' },
    { name: '조직도.xlsx', downloads: 289, owner: '최인사' },
    { name: '보안정책_가이드.pdf', downloads: 234, owner: '정보보안' },
    { name: '연차신청서.hwp', downloads: 198, owner: 'HR팀' },
    { name: '프로젝트_템플릿.pptx', downloads: 156, owner: '기획팀' }
  ],
  recentActivity: [
    { user: '김민수', action: '파일 업로드', file: '보고서_0121.pdf', time: '10분 전' },
    { user: '이영희', action: '파일 수정', file: '디자인가이드.ai', time: '25분 전' },
    { user: '박준호', action: '폴더 공유', file: '마케팅자료', time: '1시간 전' },
    { user: '최지혜', action: '파일 다운로드', file: '기획서_템플릿.pptx', time: '2시간 전' },
    { user: '정수현', action: '파일 삭제', file: '임시파일.tmp', time: '3시간 전' },
    { user: '한성민', action: '폴더 생성', file: '2024_프로젝트', time: '4시간 전' },
    { user: '오경아', action: '파일 이동', file: '예산계획서.xlsx', time: '5시간 전' }
  ]
};

const DriveDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileTypeIcon = (type) => {
    switch (type) {
      case 'word':
        return <FileIcon sx={{ color: '#1976d2' }} />;
      case 'excel':
        return <FileIcon sx={{ color: '#388e3c' }} />;
      case 'pdf':
        return <FileIcon sx={{ color: '#d32f2f' }} />;
      case 'image':
        return <FileIcon sx={{ color: '#f57c00' }} />;
      default:
        return <FileIcon sx={{ color: '#757575' }} />;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Grid container spacing={3}>
        {/* 상단 영역 - 스토리지 사용량 및 파일 분포 */}
        <Grid item xs={12} md={8}>
          {/* 스토리지 사용량 요약 */}
          <Card sx={{ height: 320, boxShadow: '0 1px 3px rgba(60,64,67,.3)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StorageIcon sx={{ color: '#1a73e8', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  스토리지 사용량
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h3" sx={{ color: '#1a73e8', fontWeight: 300, mr: 2 }}>
                  {mockData.storageUsage.percentage}%
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(mockData.storageUsage.used)} / {formatFileSize(mockData.storageUsage.total)} 사용 중
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={mockData.storageUsage.percentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#e8f0fe',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        backgroundColor: '#1a73e8'
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* 사용량 추이 차트 */}
              <Box sx={{ height: 160, mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  최근 6개월 사용량 추이
                </Typography>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mockData.storageHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="month" stroke="#5f6368" fontSize={12} />
                    <YAxis stroke="#5f6368" fontSize={12} />
                    <Tooltip
                      formatter={(value) => [`${value}GB`, '사용량']}
                      labelStyle={{ color: '#202124' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="usage"
                      stroke="#1a73e8"
                      strokeWidth={2}
                      dot={{ fill: '#1a73e8', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 우측 상단 - 파일 타입 분포 */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 320, boxShadow: '0 1px 3px rgba(60,64,67,.3)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FileIcon sx={{ color: '#34a853', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  파일 타입 분포
                </Typography>
              </Box>
              <Box sx={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={mockData.fileTypes}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name} ${value}%`}
                      labelLine={false}
                    >
                      {mockData.fileTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value}%`, '비율']} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 중앙 영역 - 상위 사용자 및 대용량 파일 */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 420, boxShadow: '0 1px 3px rgba(60,64,67,.3)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon sx={{ color: '#ea4335', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  상위 사용자 Top 7
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 380, overflow: 'hidden' }}>
                <Table size="small" sx={{ '& .MuiTableRow-root': { height: 40 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: '#5f6368' }}>순위</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#5f6368' }}>사용자</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#5f6368' }}>부서</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#5f6368' }}>사용량</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockData.topUsers.map((user, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            sx={{
                              bgcolor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#e0e0e0',
                              color: '#000',
                              fontWeight: 500,
                              fontSize: '0.75rem'
                            }}
                          />
                        </TableCell>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.department}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                          {formatFileSize(user.usage)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: 420, boxShadow: '0 1px 3px rgba(60,64,67,.3)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon sx={{ color: '#fbbc04', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  대용량 파일
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 380, overflow: 'hidden' }}>
                <Table size="small" sx={{ '& .MuiTableRow-root': { height: 40 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: '#5f6368' }}>파일명</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#5f6368' }}>소유자</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#5f6368' }}>수정일</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#5f6368' }}>크기</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockData.largeFiles.map((file, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FileIcon sx={{ color: '#d93025', mr: 1, fontSize: 20 }} />
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }} title={file.name}>
                              {file.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{file.owner}</TableCell>
                        <TableCell>{file.lastModified}</TableCell>
                        <TableCell align="right" sx={{ color: '#d93025', fontWeight: 500 }}>
                          {file.size}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* 하단 영역 - 최근 활동 및 다운로드 순위 */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 420, boxShadow: '0 1px 3px rgba(60,64,67,.3)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon sx={{ color: '#9c27b0', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  최근 문서 활동
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 380, overflow: 'hidden' }}>
                <Table size="small" sx={{ '& .MuiTableRow-root': { height: 40 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: '#5f6368' }}>사용자</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#5f6368' }}>활동</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#5f6368' }}>파일</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#5f6368' }}>시간</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockData.recentActivity.map((activity, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{activity.user}</TableCell>
                        <TableCell>{activity.action}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }} title={activity.file}>
                            {activity.file}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: '#5f6368' }}>{activity.time}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: 420, boxShadow: '0 1px 3px rgba(60,64,67,.3)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ color: '#ff9800', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  인기 다운로드
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 380, overflow: 'hidden' }}>
                <Table size="small" sx={{ '& .MuiTableRow-root': { height: 40 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: '#5f6368' }}>파일명</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#5f6368' }}>소유자</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#5f6368' }}>다운로드</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockData.topDownloads.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DownloadIcon sx={{ color: '#ff9800', mr: 1, fontSize: 20 }} />
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }} title={item.name}>
                              {item.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{item.owner}</TableCell>
                        <TableCell align="right" sx={{ color: '#ff9800', fontWeight: 500 }}>
                          {item.downloads.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );
};

export default DriveDashboard;